/**
 * The lead watch ("Vihjete valve", ring R9) on the Rikked tab of Kliendiotsing
 * (`/prospects/incidents`): the switch, the list it keeps, and the open
 * incidents an alert call could be placed on.
 *
 * A HAND-WRITTEN MIRROR of the server's watch contract:
 *  - `apps/api/src/modules/ariregister/scan-watch.routes.ts` (paths, `SiteWatchState`)
 *  - `.../ariregister/lib/scanner/watch/settings.ts` (`SiteWatchSettings`, `SITE_WATCH_LIMITS`)
 *  - `.../ariregister/lib/scanner/watch/incident-view.ts` (`IncidentView`)
 *  - `.../ariregister/lib/incidents/catalogue.ts` (`ALERT_FINDING_IDS`, `ALERT_CLASSES`)
 *  - `.../ariregister/lib/incidents/callable.ts` (`NOT_CALLABLE_REASONS`)
 * `siteWatch.test.ts` reads those files and holds the lists, bounds and paths
 * below against them, so a finding added on the server fails a test here
 * instead of rendering as a raw key.
 *
 * React-free: clamping, label keys and tones are the parts worth testing.
 */
import { api } from "./api";

export const SITE_WATCH_PATH = "/admin/ariregister/scan-watch";
export const SITE_WATCH_REFRESH_PATH = "/admin/ariregister/scan-watch/refresh";
export const INCIDENTS_PATH = "/admin/ariregister/incidents";

/** How many open incidents the card asks for. The server clamps to 1..500. */
export const OPEN_INCIDENTS_LIMIT = 50;

/** Mirrors `SITE_WATCH_LIMITS` (the route validates against the same numbers). */
export const SITE_WATCH_LIMITS = {
  homepageMinutes: { min: 15, max: 1440 },
  maxDomains: { min: 10, max: 2000 },
  concurrency: { min: 1, max: 8 },
  timeoutSeconds: { min: 5, max: 30 },
} as const;

export type SiteWatchNumberField = keyof typeof SITE_WATCH_LIMITS;

/** The number fields in the order the settings form shows them. */
export const SITE_WATCH_NUMBER_FIELDS = [
  "homepageMinutes",
  "maxDomains",
  "concurrency",
  "timeoutSeconds",
] as const satisfies readonly SiteWatchNumberField[];

/** Mirrors `SiteWatchSettings`. */
export interface SiteWatchSettings {
  enabled: boolean;
  homepageMinutes: number;
  maxDomains: number;
  concurrency: number;
  timeoutSeconds: number;
  domainExpiry: boolean;
}

/** Mirrors `SiteWatchState` (GET scan-watch). */
export interface SiteWatchState {
  settings: SiteWatchSettings;
  stats: { watched: number; due: number; failing: number; openIncidents: number; callable: number };
  /** The last control probe in the API process, or null when none ran since boot. */
  network: { ok: boolean; at: string } | null;
  lastCheckedAt: string | null;
}

/** Mirrors `DOMAIN_EXPIRY_WINDOW_DAYS`: how far ahead an expiring domain counts. */
export const DOMAIN_EXPIRY_WINDOW_DAYS = 14;

/** Mirrors `ALERT_FINDING_IDS`. */
export const ALERT_FINDING_IDS = [
  "site-unreachable",
  "server-error",
  "tls-cert-expired",
  "domain-expiring",
  "exposed-env",
  "exposed-git",
  "exposed-wp-config-backup",
  "exposed-wp-debug-log",
  "wp-core-insecure",
  "php-eol",
  "malware-blocklisted",
  "malware-defaced",
  "link_broken",
  "layout_text_cut",
  "layout_scrolls_sideways",
] as const;
export type AlertFindingId = (typeof ALERT_FINDING_IDS)[number];

/** Mirrors `ALERT_CLASSES`. */
export const ALERT_CLASSES = ["urgent", "helpful"] as const;
export type AlertClass = (typeof ALERT_CLASSES)[number];

/** Mirrors `NOT_CALLABLE_REASONS`. */
export const NOT_CALLABLE_REASONS = ["unknown_finding", "resolved", "too_old", "unconfirmed", "old_break", "called"] as const;
export type NotCallableReason = (typeof NOT_CALLABLE_REASONS)[number];

/**
 * Mirrors `IncidentView`. The id-like fields stay `string`: a newer server may
 * send a finding or reason this browser does not know yet, and the card shows
 * it raw rather than dropping the row.
 */
export interface IncidentView {
  id: string;
  domain: string;
  regCode: string | null;
  companyName: string | null;
  findingId: string;
  class: string;
  source: string;
  firstSeenAt: string;
  confirmedAt: string | null;
  lastSeenAt: string;
  resolvedAt: string | null;
  calledAt: string | null;
  callable: boolean;
  notCallable: string | null;
}

export const getSiteWatch = (): Promise<SiteWatchState> => api.get<SiteWatchState>(SITE_WATCH_PATH);

export const saveSiteWatch = (
  patch: Partial<SiteWatchSettings>,
): Promise<{ ok: boolean; settings: SiteWatchSettings }> =>
  api.put<{ ok: boolean; settings: SiteWatchSettings }>(SITE_WATCH_PATH, patch);

export const refreshSiteWatch = (): Promise<{ ok: boolean; watched: number }> =>
  api.post<{ ok: boolean; watched: number }>(SITE_WATCH_REFRESH_PATH);

export const listOpenIncidents = (limit: number = OPEN_INCIDENTS_LIMIT): Promise<{ incidents: IncidentView[] }> =>
  api.get<{ incidents: IncidentView[] }>(`${INCIDENTS_PATH}?open=1&limit=${limit}`);

/**
 * What a typed number becomes on blur: the clamped value, or the current one
 * when the text is not a number at all (so a stray letter reverts instead of
 * saving the floor).
 */
export function commitWatchNumber(
  field: SiteWatchNumberField,
  raw: string,
  current: number,
): { value: number; changed: boolean } {
  const text = raw.trim();
  const n = Number(text);
  if (!text || !Number.isFinite(n)) return { value: current, changed: false };
  const { min, max } = SITE_WATCH_LIMITS[field];
  const value = Math.min(max, Math.max(min, Math.round(n)));
  return { value, changed: value !== current };
}

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === "string" && (list as readonly string[]).includes(value);

/** `siteAlert.finding.<id>`, or null for an id this browser does not know. */
export function findingLabelKey(findingId: string): string | null {
  return isOneOf(ALERT_FINDING_IDS, findingId) ? `siteAlert.finding.${findingId}` : null;
}

/** `siteAlert.class.<class>`, or null for an unknown class. */
export function classLabelKey(cls: string): string | null {
  return isOneOf(ALERT_CLASSES, cls) ? `siteAlert.class.${cls}` : null;
}

/** `siteAlert.notCallable.<reason>`, or null for none or an unknown reason. */
export function notCallableKey(reason: string | null): string | null {
  return isOneOf(NOT_CALLABLE_REASONS, reason) ? `siteAlert.notCallable.${reason}` : null;
}

export type WatchBadgeTone = "destructive" | "info" | "success" | "muted";

/** Urgent reads as a failure, helpful as information, anything else stays quiet. */
export function classTone(cls: string): WatchBadgeTone {
  if (cls === "urgent") return "destructive";
  if (cls === "helpful") return "info";
  return "muted";
}

/** What the call column says: callable, or the reason it is not (key or raw text). */
export function incidentCallLabel(
  incident: Pick<IncidentView, "callable" | "notCallable">,
): { tone: WatchBadgeTone; key: string | null; raw: string | null } {
  if (incident.callable) return { tone: "success", key: "leads.watch.incidents.callable", raw: null };
  return { tone: "muted", key: notCallableKey(incident.notCallable), raw: incident.notCallable };
}

/** Which network sentence the card shows. */
export function networkState(network: SiteWatchState["network"]): "unknown" | "ok" | "down" {
  if (!network) return "unknown";
  return network.ok ? "ok" : "down";
}

/** The Intl locale for dates, from the interface language. */
export function dateLocale(locale: string): string {
  return locale.startsWith("et") ? "et-EE" : "en-GB";
}

/** Full date and time for a tooltip, "-" for none or an unreadable stamp. */
export function fullWhen(iso: string | null, locale: string): string {
  if (!iso) return "-";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "-";
  return at.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
}
